import type { StartupData } from '@workspace/api-backend'
import { useCallback } from 'react'
import { useMarkPostImprovementPromptShownMutation } from '../../../hooks/api'

/**
 * "Your profile just got stronger" — a full-screen nudge shown right after a
 * founder finishes an AI session, uploads a new deck, or saves a profile
 * edit. Capped at once per calendar day via `Startup.postImprovementPromptShownAt`
 * (server-side) so the cap holds across the founder's browsers/devices, not
 * just the one that triggered it.
 */
function dayKey(date: Date | string | null): string | null {
  if (!date) return null
  return new Date(date).toISOString().slice(0, 10)
}

export function usePostImprovementPrompt(startup: StartupData) {
  const markShownMutation = useMarkPostImprovementPromptShownMutation(startup.id)

  // Stable across renders — a caller may put these in a useEffect dependency
  // array (e.g. a redirect timer keyed on "should this fire"), and a
  // freshly-recreated function reference every render would retrigger that
  // effect and keep resetting the timer.
  const canShowToday = useCallback(
    () => dayKey(startup.postImprovementPromptShownAt) !== dayKey(new Date()),
    [startup.postImprovementPromptShownAt]
  )
  const markShownToday = useCallback(() => {
    markShownMutation.mutate({ path: { startupId: startup.id } })
  }, [markShownMutation, startup.id])

  return { canShowToday, markShownToday }
}
